/**
 * MemberForm — add or edit a family member (admin surface).
 *
 * Reuses the onboarding pickers (ColourSlotPicker, AllergyChipPicker,
 * DietaryChipPicker, PIN-tastaturene) so the admin form is pixel-identical to the
 * first-run wizard and the safety gate behaves the same way.
 *
 * Innlogging (#141): brukernavn og passord er innloggingen, PIN er bare
 * hurtigbytte på kjøkkenskjermen og derfor valgfri.
 *   - Opprett: brukernavn valgfritt (serveren lager et fra kortnavnet),
 *     passord valgfritt (uten kan admin sende en lenke for nytt passord
 *     senere), PIN bak «Legg til PIN for kjøkkenskjermen».
 *   - Rediger: brukernavnet kan endres; PIN er urørt til admin velger
 *     «Endre PIN». Passord endres ikke her — det gjør medlemmet selv, eller
 *     admin via «Nytt passord»-lenken i lista.
 *   - En voksen uten passord (#143): det finnes ikke lenger et
 *     husstandspassord å arve, så skjemaet sier fra om at en lenke for nytt
 *     passord trengs før den voksne kan logge inn og låse opp administrasjon.
 *
 * Safety gate (PROJECT.md): `allergies.confirmed` must be ticked before submit
 * in BOTH modes. On edit we prefill the existing allergy chips but RESET the
 * confirmation so the admin re-confirms the list is still correct — the
 * backend enforces the same z.literal(true) contract.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeUsername, type ColorSlot, type MemberAdminRow } from '@stell/shared';
import { Button } from '../../components/primitives';
import { splitAuthError, type ApiErrorLike } from '../auth/authApi';
import {
  EMPTY_NEW_PASSWORD,
  NewPasswordFields,
  OptionalPinKeypads,
  UsernameField,
  isNewPasswordValid,
  isPinPairValid,
  usernameError,
  type NewPasswordValue,
  type PinPairValue,
} from '../auth/AuthFields';
import {
  AllergyChipPicker,
  type AllergyChipPickerValue,
} from '../onboarding/components/AllergyChipPicker';
import {
  DietaryChipPicker,
  type DietaryChipPickerValue,
} from '../onboarding/components/DietaryChipPicker';
import { ColourSlotPicker } from '../onboarding/components/ColourSlotPicker';

export interface MemberFormPayload {
  name: string;
  shortName: string | null;
  role: 'admin' | 'teen' | 'child';
  birthDate: string;
  colorSlot: ColorSlot;
  allergies: { chips: AllergyChipPickerValue['chips']; custom: string[]; confirmed: true };
  dietary: DietaryChipPickerValue;
  notes: string | null;
  /** Present only when the admin set/changed the PIN. */
  pin?: string;
  pinConfirm?: string;
  /** Bare når det er fylt ut (opprett) eller endret (rediger). */
  username?: string;
  /** Bare ved opprett, og bare når admin har valgt et passord. */
  password?: string;
  passwordConfirm?: string;
}

export interface MemberFormProps {
  mode: 'create' | 'edit';
  /** Existing row when editing. */
  initial?: MemberAdminRow | undefined;
  /** Colour slots already held by OTHER active members. */
  takenSlots: ColorSlot[];
  submitting: boolean;
  /** Server error code surfaced inline (e.g. color_slot_taken). */
  /** Server error surfaced inline (e.g. color_slot_taken, weak_password + reason). */
  error: ApiErrorLike | null;
  onSubmit: (payload: MemberFormPayload) => void;
  onCancel: () => void;
}

interface Draft {
  name: string;
  shortName: string;
  role: 'admin' | 'teen' | 'child';
  birthDate: string;
  colorSlot: ColorSlot | null;
  username: string;
  password: NewPasswordValue;
  /** null = PIN-en røres ikke (rediger) / ingen PIN (opprett). */
  pin: PinPairValue | null;
  allergies: AllergyChipPickerValue;
  dietary: DietaryChipPickerValue;
  notes: string;
}

function initialDraft(initial?: MemberAdminRow): Draft {
  if (!initial) {
    return {
      name: '',
      shortName: '',
      role: 'child',
      birthDate: '',
      colorSlot: null,
      username: '',
      password: EMPTY_NEW_PASSWORD,
      pin: null,
      allergies: { chips: [], custom: [], confirmed: false },
      dietary: { chips: [], custom: [] },
      notes: '',
    };
  }
  return {
    name: initial.name,
    shortName: initial.short_name,
    role: initial.role,
    birthDate: initial.birth_date,
    colorSlot: initial.color_token as ColorSlot,
    username: initial.username ?? '',
    password: EMPTY_NEW_PASSWORD,
    pin: null,
    // Prefill chips, reset confirmation — admin must re-confirm (safety gate).
    allergies: {
      chips: initial.dietary.allergies.chips,
      custom: initial.dietary.allergies.custom,
      confirmed: false,
    },
    dietary: {
      chips: initial.dietary.dietary.chips,
      custom: initial.dietary.dietary.custom,
    },
    notes: initial.dietary.notes ?? '',
  };
}

export function MemberForm({
  mode,
  initial,
  takenSlots,
  submitting,
  error,
  onSubmit,
  onCancel,
}: MemberFormProps): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(initial));
  const usernameChanged =
    draft.username.trim() !== '' &&
    normalizeUsername(draft.username) !== (initial?.username ?? '');
  const passwordGiven = draft.password.password !== '' || draft.password.confirm !== '';
  const adminWithoutPassword =
    draft.role === 'admin' && (mode === 'create' ? !passwordGiven : !initial?.has_password);
  const formValid =
    draft.name.trim().length > 0 &&
    draft.birthDate !== '' &&
    new Date(draft.birthDate) <= new Date() &&
    draft.colorSlot !== null &&
    usernameError(draft.username) === null &&
    (!passwordGiven || isNewPasswordValid(draft.password)) &&
    isPinPairValid(draft.pin) &&
    draft.allergies.confirmed;

  // Feil som hører til et felt vises under feltet; resten nederst.
  const serverErrors = splitAuthError(t, error);

  function handleSubmit(): void {
    if (!formValid || !draft.colorSlot || submitting) return;
    const payload: MemberFormPayload = {
      name: draft.name.trim(),
      shortName: draft.shortName.trim() || null,
      role: draft.role,
      birthDate: draft.birthDate,
      colorSlot: draft.colorSlot,
      allergies: {
        chips: draft.allergies.chips,
        custom: draft.allergies.custom,
        confirmed: true,
      },
      dietary: { chips: draft.dietary.chips, custom: draft.dietary.custom },
      notes: draft.notes.trim() || null,
    };
    if (draft.pin !== null) {
      payload.pin = draft.pin.pin;
      payload.pinConfirm = draft.pin.pinConfirm;
    }
    if (usernameChanged) payload.username = normalizeUsername(draft.username);
    if (mode === 'create' && passwordGiven) {
      payload.password = draft.password.password;
      payload.passwordConfirm = draft.password.confirm;
    }
    onSubmit(payload);
  }

  return (
    <form
      data-testid="member-form"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('onboarding.step3.name')}</span>
        <input
          data-testid="member-name"
          className="field"
          type="text"
          maxLength={60}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('onboarding.step3.nickname')}</span>
        <input
          data-testid="member-short-name"
          className="field"
          type="text"
          maxLength={40}
          value={draft.shortName}
          onChange={(e) => setDraft({ ...draft, shortName: e.target.value })}
        />
      </label>

      <fieldset className="flex flex-col gap-1" data-testid="member-role">
        <legend className="text-ink">{t('onboarding.step3.role')}</legend>
        <div className="flex gap-4">
          {(['admin', 'teen', 'child'] as const).map((r) => (
            <label key={r} className="flex items-center gap-1">
              <input
                type="radio"
                name="role"
                value={r}
                checked={draft.role === r}
                onChange={() => setDraft({ ...draft, role: r })}
                data-testid={`role-${r}`}
              />
              <span>{t(`onboarding.step3.role_${r}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('onboarding.step3.birthdate')}</span>
        <input
          data-testid="member-birthdate"
          className="field"
          type="date"
          max={new Date().toISOString().slice(0, 10)}
          value={draft.birthDate}
          onChange={(e) => setDraft({ ...draft, birthDate: e.target.value })}
        />
      </label>

      <ColourSlotPicker
        value={draft.colorSlot}
        takenSlots={takenSlots}
        onChange={(slot) => setDraft({ ...draft, colorSlot: slot })}
      />

      <fieldset className="flex flex-col gap-4" data-testid="member-login-fields">
        <legend className="text-ink mb-2 font-semibold">{t('auth.login_legend')}</legend>
        <UsernameField
          label={mode === 'create' ? t('onboarding.step3.username') : t('admin.members.username')}
          value={draft.username}
          onChange={(username) => setDraft({ ...draft, username })}
          testId="member-username"
          hint={
            mode === 'create'
              ? t('admin.members.username_hint_create')
              : t('admin.members.username_hint_edit')
          }
          error={serverErrors.username}
          autoComplete="off"
        />

        {adminWithoutPassword ? (
          <p className="text-ink-2 text-sm" data-testid="member-admin-no-password">
            {t('admin.members.admin_no_password_hint')}
          </p>
        ) : null}

        {mode === 'create' ? (
          <div className="flex flex-col gap-4" data-testid="member-password-section">
            <p className="text-ink-2 text-sm">{t('onboarding.step3.password_later_hint')}</p>
            <NewPasswordFields
              value={draft.password}
              onChange={(password) => setDraft({ ...draft, password })}
              testId="member-password"
              label={t('onboarding.step3.password_optional')}
              error={serverErrors.password}
            />
          </div>
        ) : null}

        <OptionalPinKeypads
          value={draft.pin}
          onChange={(pin) => setDraft((d) => ({ ...d, pin }))}
          addTestId={mode === 'edit' ? 'member-change-pin' : 'member-add-pin'}
          addLabel={
            mode === 'create'
              ? undefined
              : initial?.has_pin
                ? t('admin.members.change_pin')
                : t('admin.members.set_pin')
          }
        />
      </fieldset>

      <AllergyChipPicker
        memberName={draft.name.trim() || t('onboarding.step3.name')}
        value={draft.allergies}
        onChange={(allergies) => setDraft({ ...draft, allergies })}
      />

      <DietaryChipPicker
        value={draft.dietary}
        onChange={(dietary) => setDraft({ ...draft, dietary })}
      />

      {serverErrors.other ? (
        <p data-testid="member-form-error" className="text-bad text-sm">
          {serverErrors.other}
        </p>
      ) : null}

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onCancel} data-testid="member-form-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!formValid || submitting}
          data-testid="member-form-save"
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
